import type { InputMessage, RemoteCursorMessage } from '../shared/datachannel';
import type {
  PointerInputBinding,
  PointerInputState,
  Point,
  ContentRect,
  ActivePress,
} from './pointer-types';
import {
  MOUSE_MOVE_INTERVAL,
  DOUBLE_CLICK_DISTANCE_PX,
  WHEEL_FLUSH_DELAY_MS,
  WHEEL_LINE_SIZE,
  MAX_PENDING_WHEEL_DELTA,
  isPointerInput,
  mapButton,
} from './pointer-types';
import { createCursorLayer } from './pointer-cursor';

type InputSender = (msg: InputMessage) => void;

export function attachPointer(
  el: HTMLVideoElement,
  send: InputSender,
  state: PointerInputState,
): PointerInputBinding {
  const pressed = new Map<number, ActivePress>();
  let lastMoveAt = 0;
  let pendingWheelX = 0;
  let pendingWheelY = 0;
  let wheelTimer: number | null = null;
  const wheelSurface = el.parentElement || el;
  const cursor = createCursorLayer(el, state);

  const contentRect = (): ContentRect | null => {
    const box = el.getBoundingClientRect();
    if (box.width <= 0 || box.height <= 0 || el.videoWidth <= 0 || el.videoHeight <= 0) {
      return null;
    }

    const scale = Math.min(box.width / el.videoWidth, box.height / el.videoHeight);
    return {
      left: box.left + (box.width - el.videoWidth * scale) / 2,
      top: box.top + (box.height - el.videoHeight * scale) / 2,
      width: el.videoWidth * scale,
      height: el.videoHeight * scale,
    };
  };

  const pointFromClient = (
    clientX: number,
    clientY: number,
    clampOutside = false,
  ): Point | null => {
    const rect = contentRect();
    if (!rect) return null;

    const x = (clientX - rect.left) / rect.width;
    const y = (clientY - rect.top) / rect.height;
    if (!clampOutside && (x < 0 || x > 1 || y < 0 || y > 1)) return null;

    return {
      x: Math.min(1, Math.max(0, x)),
      y: Math.min(1, Math.max(0, y)),
    };
  };

  const sendMove = (point: Point, force = false): void => {
    const now = performance.now();
    if (!force && now - lastMoveAt < MOUSE_MOVE_INTERVAL) return;
    lastMoveAt = now;
    send({ type: 'mousemove', x: point.x, y: point.y });
  };

  const releasePointer = (pointerId: number, event?: PointerEvent): void => {
    const press = pressed.get(pointerId);
    if (!press) return;
    event?.preventDefault();
    pressed.delete(pointerId);
    // 鼠标按钮事件始终按真实的 pointerdown/pointerup 时刻发送。双击必须
    // 保留两个完整的 down -> up 周期；把第二击延迟到 pointerup 再合成
    // 原子 click，反而可能超出系统的双击时间窗口。
    send({ type: 'mouseup', button: press.button });
    try {
      if (el.hasPointerCapture(pointerId)) el.releasePointerCapture(pointerId);
    } catch {
      // 浏览器可能已自动释放 pointer capture。
    }
  };

  const release = (): void => {
    for (const [pointerId, press] of pressed) {
      send({ type: 'mouseup', button: press.button });
      try {
        if (el.hasPointerCapture(pointerId)) el.releasePointerCapture(pointerId);
      } catch {
        // ignore
      }
    }
    pressed.clear();
    cursor.hideCursor();
  };

  const onPointerMove = (event: PointerEvent): void => {
    if (!isPointerInput(event)) return;
    const point = pointFromClient(event.clientX, event.clientY, pressed.has(event.pointerId));
    if (!point) {
      cursor.hideCursor();
      return;
    }
    const press = pressed.get(event.pointerId);
    if (press && Math.hypot(event.clientX - press.startX, event.clientY - press.startY) > DOUBLE_CLICK_DISTANCE_PX) {
      press.moved = true;
    }
    cursor.showCursorAt(point);
    sendMove(point);
  };

  const onPointerDown = (event: PointerEvent): void => {
    if (!isPointerInput(event) || pressed.has(event.pointerId)) return;
    const button = mapButton(event.button);
    const point = pointFromClient(event.clientX, event.clientY);
    if (!button || !point) return;

    event.preventDefault();
    el.focus({ preventScroll: true });
    cursor.showCursorAt(point);

    // 右键模式保留给触摸设备；在 PC 上物理右键仍然正常发送右键。
    const remoteButton = button === 'left' && state.rightMode() ? 'right' : button;
    const press: ActivePress = {
      button: remoteButton,
      startX: event.clientX,
      startY: event.clientY,
      moved: false,
    };
    pressed.set(event.pointerId, press);
    try {
      el.setPointerCapture(event.pointerId);
    } catch {
      // 不支持 pointer capture 时仍可完成普通点击。
    }
    // 坐标跟按钮一起走可靠、有序的控制通道。这样双击的第二个 down 不再
    // 依赖单独的无序 mousemove 通道，也不会被原生输入队列人为延迟。
    sendMove(point, true);
    send({ type: 'mousedown', button: remoteButton, x: point.x, y: point.y });
  };

  const onPointerUp = (event: PointerEvent): void => {
    if (!isPointerInput(event)) return;
    releasePointer(event.pointerId, event);
  };

  const onPointerCancel = (event: PointerEvent): void => {
    if (!isPointerInput(event)) return;
    releasePointer(event.pointerId, event);
  };

  const onLostPointerCapture = (event: PointerEvent): void => {
    if (!isPointerInput(event)) return;
    releasePointer(event.pointerId);
  };

  const onPointerLeave = (): void => {
    if (pressed.size === 0) cursor.hideCursor();
  };

  const flushWheel = (): void => {
    wheelTimer = null;
    const deltaX = pendingWheelX;
    const deltaY = pendingWheelY;
    pendingWheelX = 0;
    pendingWheelY = 0;
    if (deltaX === 0 && deltaY === 0) return;
    send({ type: 'wheel', deltaX, deltaY });
  };

  const queueWheel = (deltaX: number, deltaY: number): void => {
    if (Number.isFinite(deltaX)) {
      pendingWheelX = Math.max(
        -MAX_PENDING_WHEEL_DELTA,
        Math.min(MAX_PENDING_WHEEL_DELTA, pendingWheelX + deltaX),
      );
    }
    if (Number.isFinite(deltaY)) {
      pendingWheelY = Math.max(
        -MAX_PENDING_WHEEL_DELTA,
        Math.min(MAX_PENDING_WHEEL_DELTA, pendingWheelY + deltaY),
      );
    }
    if (wheelTimer === null) {
      // 4ms 只用于把同一小段滚轮手势合成一条消息，不等待动画帧，避免
      // 触控板或高回报率鼠标产生大量小事件，也避免每个事件都单独走网络。
      wheelTimer = window.setTimeout(flushWheel, WHEEL_FLUSH_DELAY_MS);
    }
  };

  const clearPendingWheel = (): void => {
    if (wheelTimer !== null) {
      window.clearTimeout(wheelTimer);
      wheelTimer = null;
    }
    pendingWheelX = 0;
    pendingWheelY = 0;
  };

  const onWheel = (event: WheelEvent): void => {
    // 黑边和没有视频帧的区域不应影响远端鼠标。
    const point = pointFromClient(event.clientX, event.clientY);
    if (!point) {
      cursor.hideCursor();
      return;
    }
    cursor.showCursorAt(point);
    event.preventDefault();

    // DOM_DELTA_LINE/PAGE 在不同浏览器中的量纲不同，统一为接近像素的值。
    // 40px 约等于一个原生滚动步长，后端还会根据目标系统做步长适配。
    const factor = event.deltaMode === 1
      ? WHEEL_LINE_SIZE
      : event.deltaMode === 2
        ? Math.max(el.clientHeight, 1)
        : 1;
    // 某些 macOS/Electron 组合会给出 delta 为 0、但仍提供 legacy wheelDelta
    // 的事件；保留这个极窄的回退，避免滚轮“有手感但完全不动”。
    const rawDeltaX = event.deltaX !== 0 ? event.deltaX : -((event as WheelEvent & { wheelDeltaX?: number }).wheelDeltaX || 0);
    const rawDeltaY = event.deltaY !== 0 ? event.deltaY : -((event as WheelEvent & { wheelDeltaY?: number }).wheelDeltaY || 0);
    const deltaX = rawDeltaX * factor;
    const deltaY = rawDeltaY * factor;
    if (deltaX === 0 && deltaY === 0) return;
    queueWheel(deltaX, deltaY);
  };

  const onContextMenu = (event: MouseEvent): void => {
    if (contentRect()) event.preventDefault();
  };

  const onWindowBlur = (): void => release();
  const onVisibilityChange = (): void => {
    if (document.visibilityState !== 'visible') release();
  };
  const onResize = (): void => {
    cursor.refresh();
  };

  el.addEventListener('pointermove', onPointerMove);
  el.addEventListener('pointerdown', onPointerDown);
  el.addEventListener('pointerup', onPointerUp);
  el.addEventListener('pointercancel', onPointerCancel);
  el.addEventListener('lostpointercapture', onLostPointerCapture);
  el.addEventListener('pointerleave', onPointerLeave);
  // 监听视频容器的捕获阶段：虚拟键盘、文件拖拽层等覆盖物存在时，滚轮仍
  // 能稳定到达远端，不依赖事件最终落在 video 元素本身。
  wheelSurface.addEventListener('wheel', onWheel, { passive: false, capture: true });
  el.addEventListener('contextmenu', onContextMenu);
  window.addEventListener('blur', onWindowBlur);
  document.addEventListener('visibilitychange', onVisibilityChange);
  window.addEventListener('pagehide', onWindowBlur);
  window.addEventListener('resize', onResize);

  return {
    release,
    hide: () => cursor.hideCursor(),
    refresh: () => cursor.refresh(),
    setRemoteCursor: (message: RemoteCursorMessage) => cursor.setRemoteCursor(message),
    setRemoteCursorMode: (mode: 'captured' | 'overlay') => cursor.setRemoteCursorMode(mode),
    clearRemoteCursor: () => cursor.clearRemoteCursor(),
    dispose: () => {
      release();
      el.removeEventListener('pointermove', onPointerMove);
      el.removeEventListener('pointerdown', onPointerDown);
      el.removeEventListener('pointerup', onPointerUp);
      el.removeEventListener('pointercancel', onPointerCancel);
      el.removeEventListener('lostpointercapture', onLostPointerCapture);
      el.removeEventListener('pointerleave', onPointerLeave);
      wheelSurface.removeEventListener('wheel', onWheel, { capture: true });
      el.removeEventListener('contextmenu', onContextMenu);
      window.removeEventListener('blur', onWindowBlur);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onWindowBlur);
      window.removeEventListener('resize', onResize);
      clearPendingWheel();
      cursor.dispose();
    },
  };
}
